<template>
  <div class="trends-dashboard">
    <vsud-loader v-if="loader.dashboard"></vsud-loader>
    <div class="processed-data" v-else>
      
      <!-- Row 1: Summary Cards -->
      <div class="row mb-4">
        <div class="col-lg-3 col-md-6 mb-3">
          <div class="card summary-card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-sm text-muted mb-1">Total Pensioners</p>
                  <h4 class="mb-0 text-dark">{{ stats.totalPensioners.toLocaleString() }}</h4>
                </div>
                <div class="icon-box bg-gradient-primary">
                  <i class="fas fa-users"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-3 col-md-6 mb-3">
          <div class="card summary-card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-sm text-muted mb-1">Total Monthly Pension</p>
                  <h4 class="mb-0 text-dark">₦{{ formatAmount(stats.monthlyPayroll) }}</h4>
                  <p class="text-xs mb-0 mt-1">
                    <span class="badge bg-warning me-1" title="State">S: ₦{{ formatAmount(stats.stateMonthlyPension) }}</span>
                    <span class="badge bg-info" title="LGA">LG: ₦{{ formatAmount(stats.lgaMonthlyPension) }}</span>
                  </p>
                </div>
                <div class="icon-box bg-gradient-success">
                  <i class="fas fa-money-bill-wave"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-3 col-md-6 mb-3">
          <div class="card summary-card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-sm text-muted mb-1">New Retirements</p>
                  <h4 class="mb-0 text-dark">{{ stats.newRetirements.toLocaleString() }}</h4>
                  <p class="text-xs mb-0 mt-1">
                    <span class="text-info"><i class="fas fa-calendar me-1"></i>This Month</span>
                  </p>
                </div>
                <div class="icon-box bg-gradient-info">
                  <i class="fas fa-user-plus"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-3 col-md-6 mb-3">
          <div class="card summary-card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-sm text-muted mb-1">Min / Max Pension</p>
                  <h4 class="mb-0 text-dark text-sm">
                    <span class="text-success">₦{{ formatAmount(kpiMinPension) }}</span>
                    <span class="text-muted mx-1">/</span>
                    <span class="text-danger">₦{{ formatAmount(kpiMaxPension) }}</span>
                  </h4>
                  <p class="text-xs mb-0 mt-1">
                    <span class="text-warning"><i class="fas fa-chart-line me-1"></i>Monthly pension</span>
                  </p>
                </div>
                <div class="icon-box bg-gradient-warning">
                  <i class="fas fa-calculator"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 2: Retirement Trends & pension trends -->
      <div class="row mb-4">
        <div class="col-lg-8 col-md-12 mb-3">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0">Retirement by count</h6>
                  <p class="text-sm text-muted mb-0">
                    {{ retirementFilter === 'yearly'
                      ? 'Retirements by year of retirement, last 10 years (State vs LGA)'
                      : 'Retirements by month of retirement, last 12 months (State vs LGA)' }}
                  </p>
                </div>
                <div class="btn-group btn-group-sm" role="group">
                  <button type="button" :class="['btn', retirementFilter === 'monthly' ? 'btn-info' : 'btn-outline-info']" @click="retirementFilter = 'monthly'">Monthly</button>
                  <button type="button" :class="['btn', retirementFilter === 'yearly' ? 'btn-info' : 'btn-outline-info']" @click="retirementFilter = 'yearly'">Yearly</button>
                </div>
              </div>
            </div>
            <div class="card-body" style="height: 350px;">
              <div v-if="charts.retirements.categories.length > 0" class="h-100">
                <bar-column 
                  chartYaxisTitle="Retirements" 
                  :chartData="charts.retirements"
                  chartType="bar"
                  :formatNumbers="false"
                ></bar-column>
              </div>
              <div v-else class="chart-placeholder d-flex align-items-center justify-content-center h-100">
                <div class="text-center">
                  <i class="fas fa-spinner fa-spin fa-2x text-muted mb-3"></i>
                  <p class="text-muted">Loading retirement trends...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4 col-md-12 mb-3">
          <div class="card">
            <div class="card-header pb-0">
              <h6 class="mb-0">Pensioners by type</h6>
              <p class="text-sm text-muted mb-0">State vs LGA distribution</p>
            </div>
            <div class="card-body" style="height: 350px;">
              <div class="p-3">
                <table class="table table-sm">
                  <tbody>
                    <tr>
                      <td><span class="badge bg-warning me-2">&nbsp;</span> State Pensioners</td>
                      <td class="text-end fw-bold">{{ mockData.retirementsByType.state.toLocaleString() }}</td>
                      <td class="text-end text-muted">{{ calculatePercent(mockData.retirementsByType.state, mockData.retirementsByType.total) }}%</td>
                    </tr>
                    <tr>
                      <td><span class="badge bg-info me-2">&nbsp;</span> LGA Pensioners</td>
                      <td class="text-end fw-bold">{{ mockData.retirementsByType.lga.toLocaleString() }}</td>
                      <td class="text-end text-muted">{{ calculatePercent(mockData.retirementsByType.lga, mockData.retirementsByType.total) }}%</td>
                    </tr>
                    <tr class="table-light">
                      <td><strong>Total</strong></td>
                      <td class="text-end fw-bold">{{ mockData.retirementsByType.total.toLocaleString() }}</td>
                      <td class="text-end text-muted">100%</td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="charts.retirementsByType.datasets.length > 0" class="mt-2">
                  <pie 
                    :chartData="charts.retirementsByType"
                    chartType="donut"
                    :chartHeight="180"
                  ></pie>
                </div>
                <div v-else class="chart-placeholder d-flex align-items-center justify-content-center mt-4" style="height: 150px;">
                  <div class="text-center">
                    <i class="fas fa-spinner fa-spin fa-2x text-muted mb-2"></i>
                    <p class="text-xs text-muted mb-0">Loading...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 3: Pension amount trends -->
      <div class="row mb-4">
        <div class="col-lg-8 col-md-12 mb-3">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0">Pension Amount</h6>
                  <p class="text-sm text-muted mb-0">Total monthly pension</p>
                </div>
                <div class="btn-group btn-group-sm" role="group">
                  <button type="button" :class="['btn', payrollFilter === '6months' ? 'btn-success' : 'btn-outline-success']" @click="payrollFilter = '6months'">6 Months</button>
                  <button type="button" :class="['btn', payrollFilter === '12months' ? 'btn-success' : 'btn-outline-success']" @click="payrollFilter = '12months'">12 Months</button>
                </div>
              </div>
            </div>
            <div class="card-body" style="height: 350px;">
              <div v-if="charts.payroll.categories.length > 0" class="h-100">
                <bar-column 
                  chartYaxisTitle="Pension (₦)" 
                  :chartData="charts.payroll"
                  chartType="line"
                ></bar-column>
              </div>
              <div v-else class="chart-placeholder d-flex align-items-center justify-content-center h-100">
                <div class="text-center">
                  <i class="fas fa-spinner fa-spin fa-2x text-muted mb-3"></i>
                  <p class="text-muted">Loading pension trends...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4 col-md-12 mb-3">
          <div class="card">
            <div class="card-header pb-0">
              <h6 class="mb-0">Pension by Bank</h6>
              <p class="text-sm text-muted mb-0">Top 5 banks by pension payout</p>
            </div>
            <div class="card-body" style="height: 350px; overflow-y: auto;">
              <div class="p-2">
                <div v-for="(bank, index) in mockData.payrollByBank" :key="index" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span class="text-sm">{{ bank.name }}</span>
                    <span class="text-sm fw-bold">₦{{ formatAmount(bank.amount) }}</span>
                  </div>
                  <div class="progress" style="height: 8px;">
                    <div class="progress-bar bg-warning" :style="{ width: bank.statePercent + '%' }" title="State"></div>
                    <div class="progress-bar bg-info" :style="{ width: bank.lgaPercent + '%' }" title="LGA"></div>
                  </div>
                  <div class="d-flex justify-content-between">
                    <small class="text-muted">
                      <span class="text-warning">S:</span> {{ bank.stateCount.toLocaleString() }}
                      <span class="text-info ms-1">LG:</span> {{ bank.lgaCount.toLocaleString() }}
                    </small>
                    <small class="text-muted">{{ bank.count.toLocaleString() }} pensioners</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 4: Average Pension Trends (full width) -->
      <div class="row mb-4">
        <div class="col-12 mb-3">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-start flex-wrap">
                <div>
                  <h6 class="mb-0">Average Pension</h6>
                  <p class="text-sm text-muted mb-0">Average, min and max pension by year of retirement (last 10 years)</p>
                </div>
                <div class="text-end">
                  <router-link
                    v-if="pensionExtremes.min"
                    :to="{ name: 'View Pensioner', params: { id: pensionExtremes.min.id }, query: { view: 'summary' } }"
                    class="badge bg-gradient-success me-1 text-decoration-none extreme-badge"
                    :title="'Lowest pension - ' + extremeName(pensionExtremes.min)">
                    <i class="fas fa-arrow-down me-1"></i>Min: ₦{{ formatAmount(pensionExtremes.min.monthlyPension) }}
                    <span class="d-block fw-normal opacity-9" style="font-size:10px;">{{ extremeName(pensionExtremes.min) }}</span>
                  </router-link>
                  <span v-else class="badge bg-gradient-success me-1" title="Lowest monthly pension">
                    Min: ₦{{ formatAmount(overallMinPension) }}
                  </span>

                  <router-link
                    v-if="pensionExtremes.max"
                    :to="{ name: 'View Pensioner', params: { id: pensionExtremes.max.id }, query: { view: 'summary' } }"
                    class="badge bg-gradient-danger text-decoration-none extreme-badge"
                    :title="'Highest pension - ' + extremeName(pensionExtremes.max)">
                    <i class="fas fa-arrow-up me-1"></i>Max: ₦{{ formatAmount(pensionExtremes.max.monthlyPension) }}
                    <span class="d-block fw-normal opacity-9" style="font-size:10px;">{{ extremeName(pensionExtremes.max) }}</span>
                  </router-link>
                  <span v-else class="badge bg-gradient-danger" title="Highest monthly pension">
                    Max: ₦{{ formatAmount(overallMaxPension) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="card-body" style="height: 300px;">
              <div v-if="charts.avgPension.categories.length > 0" class="h-100">
                <bar-column 
                  chartYaxisTitle="Amount (₦)" 
                  :chartData="charts.avgPension"
                  chartType="line"
                ></bar-column>
              </div>
              <div v-else class="chart-placeholder d-flex align-items-center justify-content-center h-100">
                <div class="text-center">
                  <i class="fas fa-spinner fa-spin fa-2x text-muted mb-3"></i>
                  <p class="text-muted">Loading avg pension trends...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 mb-3">
          <div class="card">
            <div class="card-header pb-0">
              <h6 class="mb-0">State vs LGA Pensioners</h6>
              <p class="text-sm text-muted mb-0">Monthly pensioners distribution</p>
            </div>
            <div class="card-body" style="height: 300px;">
              <div v-if="charts.payrollComparison.categories.length > 0" class="h-100">
                <bar-column 
                  chartYaxisTitle="Pensioners" 
                  :chartData="charts.payrollComparison"
                  chartType="bar"
                  :formatNumbers="false"
                ></bar-column>
              </div>
              <div v-else class="chart-placeholder d-flex align-items-center justify-content-center h-100">
                <div class="text-center">
                  <i class="fas fa-spinner fa-spin fa-2x text-muted mb-3"></i>
                  <p class="text-muted">Loading pension comparison...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 5: Detailed Statistics Tables -->
      <div class="row mb-4">
        <div class="col-lg-6 col-md-12 mb-3">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="mb-0">Retirements by LGA</h6>
                  <p class="text-sm text-muted mb-0">Verified pensioners by Local Government of origin (residence LGA where origin is not recorded)</p>
                </div>
                <div class="btn-group btn-group-sm" role="group">
                  <button type="button" :class="['btn', originLgaScope === 'all' ? 'btn-info' : 'btn-outline-info']" @click="originLgaScope = 'all'">Total</button>
                  <button type="button" :class="['btn', originLgaScope === 'lga' ? 'btn-info' : 'btn-outline-info']" @click="originLgaScope = 'lga'">LGA</button>
                  <button type="button" :class="['btn', originLgaScope === 'state' ? 'btn-info' : 'btn-outline-info']" @click="originLgaScope = 'state'">State</button>
                </div>
              </div>
            </div>
            <div class="card-body" style="height: 350px; overflow-y: auto;">
              <table class="table table-sm table-hover">
                <thead>
                  <tr>
                    <th class="text-xs text-uppercase text-muted">#</th>
                    <th class="text-xs text-uppercase text-muted">LGA</th>
                    <th class="text-xs text-uppercase text-muted text-end">Count</th>
                    <th class="text-xs text-uppercase text-muted text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in originLgaRows" :key="index">
                    <td class="text-sm">{{ index + 1 }}</td>
                    <td class="text-sm">{{ item.name }}</td>
                    <td class="text-sm text-end fw-bold">{{ item.count.toLocaleString() }}</td>
                    <td class="text-sm text-end text-success">₦{{ formatAmount(item.amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="col-lg-6 col-md-12 mb-3">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-0">Monthly Summary</h6>
                  <p class="text-sm text-muted mb-0">Last 6 months by month of retirement</p>
                </div>
              </div>
            </div>
            <div class="card-body" style="height: 350px; overflow-y: auto;">
              <table class="table table-sm table-hover">
                <thead>
                  <tr>
                    <th class="text-xs text-uppercase text-muted">Month</th>
                    <th class="text-xs text-uppercase text-muted text-end">New</th>
                    <th class="text-xs text-uppercase text-muted text-end">Verified</th>
                    <th class="text-xs text-uppercase text-muted text-end">Pension</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in mockData.monthlySummary" :key="index">
                    <td class="text-sm">{{ item.month }}</td>
                    <td class="text-sm text-end">{{ item.newPensioners.toLocaleString() }}</td>
                    <td class="text-sm text-end text-success">{{ item.verified.toLocaleString() }}</td>
                    <td class="text-sm text-end fw-bold">₦{{ formatAmount(item.payroll) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Data Status Banner -->
      <div class="row" v-if="!loader.dashboard && chartData.retirements.length === 0">
        <div class="col-12">
          <div class="alert alert-warning d-flex align-items-center">
            <i class="fas fa-exclamation-triangle fa-2x me-3"></i>
            <div>
              <strong>Note:</strong> Some chart data may not be available. 
              This could be due to no data for the selected time period.
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import VsudLoader from "@/components/VsudLoader.vue";
import AuthService from "@/services/auth.service";
import BarColumn from '@/views/components/dashboard/charts/appexchart/BarColumn.vue';
import Pie from '@/views/components/dashboard/charts/appexchart/PieDonut.vue';

export default {
  name: "dashboard-trends",
  components: {
    VsudLoader,
    BarColumn,
    Pie
  },
  data() {
    return {
      loader: {
        dashboard: true
      },
      retirementFilter: 'monthly',
      payrollFilter: '12months',

      // Actual pensioner records holding the lowest / highest monthly pension
      pensionExtremes: {
        min: null,
        max: null
      },

      originLgaScope: 'all',

      // Summary Stats
      stats: {
        totalPensioners: 0,
        monthlyPayroll: 0,
        stateMonthlyPension: 0,
        lgaMonthlyPension: 0,
        newRetirements: 0,
        avgPension: 0
      },

      // Data for various sections
      mockData: {
        retirementsByType: {
          state: 0,
          lga: 0,
          total: 0
        },
        
        payrollByBank: [],

        retirementsByLGA: [],

        monthlySummary: []
      },

      // Chart data (raw from API)
      chartData: {
        retirements: [],
        retirementsByYear: [],
        payroll: [],
        avgPension: [],
        payrollComparison: []
      },

      // Formatted chart data for ApexCharts
      charts: {
        retirements: { categories: [], datasets: [] },
        payroll: { categories: [], datasets: [] },
        avgPension: { categories: [], datasets: [] },
        payrollComparison: { categories: [], datasets: [] },
        retirementsByType: { labels: [], datasets: [] }
      }
    };
  },

  mounted() {
    this.loadAllData();
  },

  computed: {
    overallMinPension() {
      const mins = (this.chartData.avgPension || [])
        .map(item => parseFloat(item.min_pension))
        .filter(v => !Number.isNaN(v) && v > 0);
      return mins.length ? Math.min(...mins) : 0;
    },
    overallMaxPension() {
      const maxes = (this.chartData.avgPension || [])
        .map(item => parseFloat(item.max_pension))
        .filter(v => !Number.isNaN(v));
      return maxes.length ? Math.max(...maxes) : 0;
    },
    kpiMinPension() {
      if (this.pensionExtremes.min && this.pensionExtremes.min.monthlyPension != null) {
        return parseFloat(this.pensionExtremes.min.monthlyPension) || 0;
      }
      return this.overallMinPension;
    },
    kpiMaxPension() {
      if (this.pensionExtremes.max && this.pensionExtremes.max.monthlyPension != null) {
        return parseFloat(this.pensionExtremes.max.monthlyPension) || 0;
      }
      return this.overallMaxPension;
    },
    originLgaRows() {
      const scope = this.originLgaScope;
      return (this.mockData.retirementsByLGA || [])
        .map((item) => {
          const stateCount = parseInt(item.stateCount, 10) || 0;
          const lgaCount = parseInt(item.lgaCount, 10) || 0;
          const count = scope === 'lga'
            ? lgaCount
            : scope === 'state'
              ? stateCount
              : (parseInt(item.count, 10) || 0);
          const stateAmount = parseFloat(item.stateAmount) || 0;
          const lgaAmount = parseFloat(item.lgaAmount) || 0;
          const amount = scope === 'lga'
            ? lgaAmount
            : scope === 'state'
              ? stateAmount
              : (parseFloat(item.amount) || 0);
          return { ...item, count, amount };
        })
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count);
    }
  },

  methods: {
    async loadAllData() {
      this.loader.dashboard = true;
      try {
        // Load all data in parallel
        await Promise.all([
          this.loadSummary(),
          this.loadRetirementsByType(),
          this.loadPayrollByBank(),
          this.loadRetirementsByLga(),
          this.loadMonthlySummary(),
          this.loadRetirementTrends(),
          this.loadRetirementsByYear(),
          this.loadPayrollTrends(),
          this.loadAvgPensionTrends(),
          this.loadPayrollComparison(),
          this.loadPensionExtremes()
        ]);
      } catch (error) {
        console.error('Error loading trends data:', error);
      } finally {
        this.loader.dashboard = false;
      }
    },

    async loadSummary() {
      try {
        const response = await AuthService.getTrendsSummary();
        if (response && response.data) {
          this.stats.totalPensioners = response.data.total_pensioners || 0;
          this.stats.monthlyPayroll = response.data.monthly_payroll || 0;
          this.stats.stateMonthlyPension = parseFloat(response.data.state_monthly_pension) || 0;
          this.stats.lgaMonthlyPension = parseFloat(response.data.lga_monthly_pension) || 0;
          this.stats.newRetirements = response.data.new_retirements_this_month || 0;
          this.stats.avgPension = response.data.avg_pension || 0;
        }
      } catch (error) {
        console.error('Error loading summary:', error);
      }
    },

    async loadRetirementsByType() {
      try {
        const response = await AuthService.getTrendsRetirementsByType();
        if (response && response.data) {
          let stateCount = 0;
          let lgaCount = 0;
          response.data.forEach(item => {
            if (item.type === 'State') {
              stateCount = item.count;
            } else if (item.type === 'LGA') {
              lgaCount = item.count;
            }
          });
          this.mockData.retirementsByType = {
            state: stateCount,
            lga: lgaCount,
            total: stateCount + lgaCount
          };
          // Format for ApexCharts Pie
          this.charts.retirementsByType = {
            labels: ['State', 'LGA'],
            datasets: [stateCount, lgaCount]
          };
        }
      } catch (error) {
        console.error('Error loading retirements by type:', error);
      }
    },

    async loadPayrollByBank() {
      try {
        const response = await AuthService.getTrendsPayrollByBank(5);
        if (response && response.data) {
          const maxAmount = response.data[0]?.total_payroll || 1;
          this.mockData.payrollByBank = response.data.map(item => {
            const total = parseFloat(item.total_payroll) || 0;
            const statePay = parseFloat(item.state_payroll) || 0;
            const lgaPay = parseFloat(item.lga_payroll) || 0;
            return {
              name: item.bank_name,
              amount: total,
              count: item.pensioner_count,
              stateCount: item.state_count || 0,
              lgaCount: item.lga_count || 0,
              statePercent: Math.round((statePay / maxAmount) * 100),
              lgaPercent: Math.round((lgaPay / maxAmount) * 100)
            };
          });
        }
      } catch (error) {
        console.error('Error loading payroll by bank:', error);
      }
    },

    async loadRetirementsByLga() {
      try {
        const response = await AuthService.getTrendsRetirementsByLga(0);
        if (response && response.data) {
          this.mockData.retirementsByLGA = response.data.map(item => ({
            name: item.lga,
            count: item.pensioner_count,
            stateCount: item.state_count,
            lgaCount: item.lga_count,
            amount: parseFloat(item.total_payroll) || 0,
            stateAmount: parseFloat(item.state_payroll) || 0,
            lgaAmount: parseFloat(item.lga_payroll) || 0
          }));
        }
      } catch (error) {
        console.error('Error loading retirements by LGA:', error);
      }
    },

    async loadMonthlySummary() {
      try {
        const response = await AuthService.getTrendsMonthlySummary(6);
        if (response && response.data) {
          this.mockData.monthlySummary = response.data.map(item => ({
            month: this.formatMonth(item.month),
            newPensioners: item.total_pensioners || 0,
            verified: item.state_pensioners + item.lga_pensioners || 0,
            payroll: parseFloat(item.total_payroll) || 0
          }));
        }
      } catch (error) {
        console.error('Error loading monthly summary:', error);
      }
    },

    async loadRetirementTrends() {
      try {
        const response = await AuthService.getTrendsRetirements(12);
        if (response && response.data) {
          this.chartData.retirements = response.data;
          this.buildRetirementChart();
        }
      } catch (error) {
        console.error('Error loading retirement trends:', error);
      }
    },

    async loadRetirementsByYear() {
      try {
        const response = await AuthService.getTrendsRetirementsByYear();
        if (response && response.data) {
          this.chartData.retirementsByYear = response.data;
          this.buildRetirementChart();
        }
      } catch (error) {
        console.error('Error loading retirements by year:', error);
      }
    },

    buildRetirementChart() {
      if (this.retirementFilter === 'yearly') {
        const data = this.chartData.retirementsByYear || [];
        this.charts.retirements = {
          categories: data.map(item => String(item.year)),
          datasets: [
            { name: 'State', data: data.map(item => parseInt(item.state_count) || 0) },
            { name: 'LGA', data: data.map(item => parseInt(item.lga_count) || 0) }
          ]
        };
      } else {
        const data = this.chartData.retirements || [];
        this.charts.retirements = {
          categories: data.map(item => this.formatMonth(item.month)),
          datasets: [
            { name: 'State', data: data.map(item => parseInt(item.state_count) || 0) },
            { name: 'LGA', data: data.map(item => parseInt(item.lga_count) || 0) }
          ]
        };
      }
    },

    async loadPayrollTrends() {
      try {
        const months = this.payrollFilter === '6months' ? 6 : 12;
        const response = await AuthService.getTrendsPayroll(months);
        if (response && response.data) {
          this.chartData.payroll = response.data;
          // State vs LGA lines by month of retirement
          this.charts.payroll = {
            categories: response.data.map(item => this.formatMonth(item.month)),
            datasets: [
              { name: 'State', data: response.data.map(item => parseFloat(item.state_payroll) || 0) },
              { name: 'LGA', data: response.data.map(item => parseFloat(item.lga_payroll) || 0) }
            ]
          };
        }
      } catch (error) {
        console.error('Error loading payroll trends:', error);
      }
    },

    async loadAvgPensionTrends() {
      try {
        // Annual average / min / max by retirement year (last 10 years)
        const response = await AuthService.getTrendsAvgPension(120, 'year');
        if (response && response.data) {
          this.chartData.avgPension = response.data;
          this.charts.avgPension = {
            categories: response.data.map(item => String(item.year)),
            datasets: [
              { name: 'Average', data: response.data.map(item => parseFloat(item.avg_pension) || 0) },
              { name: 'Min', data: response.data.map(item => parseFloat(item.min_pension) || 0) },
              { name: 'Max', data: response.data.map(item => parseFloat(item.max_pension) || 0) }
            ]
          };
        }
      } catch (error) {
        console.error('Error loading avg pension trends:', error);
      }
    },

    async loadPensionExtremes() {
      // Fetch the actual pensioner with the lowest and highest monthly pension
      // (verified only) by asking the API to sort and return a single record.
      try {
        const [maxRes, minRes] = await Promise.all([
          AuthService.getPensioners('?perPage=1&status=completed&sort=monthly_pension&dir=desc'),
          AuthService.getPensioners('?perPage=1&status=completed&min_amount=1&sort=monthly_pension&dir=asc')
        ]);
        this.pensionExtremes.max = (maxRes && maxRes.data && maxRes.data[0]) || null;
        this.pensionExtremes.min = (minRes && minRes.data && minRes.data[0]) || null;
      } catch (error) {
        console.error('Error loading pension extremes:', error);
      }
    },

    extremeName(rec) {
      if (!rec) return '';
      const name = [rec.firstName, rec.lastName].filter(Boolean).join(' ').trim();
      const id = rec.pensionId || rec.uniquePensionerId || rec.unique_pensioner_id || '';
      return [name, id].filter(Boolean).join(' · ');
    },

    async loadPayrollComparison() {
      try {
        const response = await AuthService.getTrendsPayrollComparison(12);
        if (response && response.data) {
          this.chartData.payrollComparison = response.data;
          // Format for ApexCharts
          this.charts.payrollComparison = {
            categories: response.data.map(item => this.formatMonth(item.month)),
            datasets: [
              { name: 'State', data: response.data.map(item => parseInt(item.state_count) || 0) },
              { name: 'LGA', data: response.data.map(item => parseInt(item.lga_count) || 0) }
            ]
          };
        }
      } catch (error) {
        console.error('Error loading payroll comparison:', error);
      }
    },

    formatMonth(monthStr) {
      if (!monthStr) return '';
      const [year, month] = monthStr.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    },

    formatAmount(amount) {
      if (amount >= 1000000000) {
        return (amount / 1000000000).toFixed(2) + 'B';
      } else if (amount >= 1000000) {
        return (amount / 1000000).toFixed(2) + 'M';
      } else if (amount >= 1000) {
        return (amount / 1000).toFixed(2) + 'K';
      }
      return amount.toLocaleString();
    },

    calculatePercent(value, total) {
      if (!total) return 0;
      return ((value / total) * 100).toFixed(1);
    }
  },

  watch: {
    payrollFilter() {
      this.loadPayrollTrends();
    },
    retirementFilter() {
      this.buildRetirementChart();
    }
  }
};
</script>

<style scoped>
.trends-dashboard {
  padding: 0;
}

.summary-card {
  border: none;
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.icon-box {
  width: 48px;
  height: 48px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
}

.card {
  border: none;
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.card-header {
  background: transparent;
  border-bottom: 1px solid #f0f0f0;
  padding: 1.25rem;
}

.card-header h6 {
  font-weight: 600;
  color: #344767;
}

.card-body {
  padding: 1.25rem;
}

.chart-placeholder {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0.75rem;
  border: 2px dashed #dee2e6;
}

.table th {
  border-top: none;
  font-weight: 600;
}

.table td {
  vertical-align: middle;
}

.progress {
  border-radius: 0.5rem;
  background-color: #e9ecef;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
}

code {
  background-color: #f8f9fa;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  color: #e91e63;
}

.alert-info {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border: none;
  border-radius: 1rem;
  color: #1565c0;
}

.alert-info code {
  background-color: rgba(255, 255, 255, 0.7);
  color: #1565c0;
}

.chart-data-display {
  height: 100%;
  overflow-y: auto;
}

.chart-data-display .table {
  margin-bottom: 0;
}

.chart-data-display .table th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.alert-warning {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border: none;
  border-radius: 1rem;
  color: #e65100;
}
</style>
